import React, { useCallback, MouseEvent, useRef } from 'react'

import styled from '@emotion/styled'

import { useEventCapture } from '@surglogs-app/analytics'
import { useHandler } from '@surglogs-app/hooks'
import { Cross } from '@surglogs-app/icons'
import { absoluteCenter, Box, Icon, useLayerManager } from '@surglogs-app/ui'
import { isClickOutsideRef } from '@surglogs-app/util'

import { PromoModalContext } from '../constants/Analytics'
import { useTranslation } from '../services'
import { PromoModalFeature } from '../types'

const VIDEO_MAX_WIDTH = 1200

const SheetWrapper = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`

const VideoContainer = styled(Box)`
  position: relative;
  width: 80vw;
  max-width: ${VIDEO_MAX_WIDTH}px;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio, trick to get rid of black bars */

  & video {
    ${absoluteCenter()}
    width: 100%;
    height: auto;
    border-radius: ${({ theme }) => theme.ui.spacings.md};
  }
`

interface PromoModalVideoProps {
  hideModal: () => void
  videoUrl: string
  featureName: PromoModalFeature
  context: PromoModalContext
}

export const PromoModalVideo = ({
  hideModal,
  videoUrl,
  featureName,
  context,
}: PromoModalVideoProps) => {
  const { t } = useTranslation()
  const { increaseBy, zIndex } = useLayerManager()
  const contentRef = useRef<HTMLDivElement>(null)
  const { captureEvent } = useEventCapture()

  const onClose = useHandler(() => {
    hideModal()
    captureEvent('promo_video_closed', {
      type: featureName,
      context,
    })
  })

  const onBackgroundClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isClickOutsideRef(contentRef, e)) {
        onClose()
      }
    },
    [onClose],
  )

  return (
    <SheetWrapper
      {...{ zi: zIndex + increaseBy * 2, onClick: onBackgroundClick }}>
      <VideoContainer {...{ role: 'group', 'aria-label': 'promo-video' }}>
        <Box {...{ ref: contentRef }}>
          <video
            {...{
              controls: true,
              controlsList: 'nodownload',
              autoPlay: true,
            }}>
            <source {...{ src: videoUrl, type: 'video/mp4' }} />
            <track {...{ kind: 'captions' }} />
            {t('video.unsupportedMessage')}
          </video>
        </Box>
      </VideoContainer>

      <Icon
        {...{
          icon: Cross,
          position: 'absolute',
          top: 'md',
          right: 'md',
          variant: 'white.100',
          size: 'xxl',
          cursor: 'pointer',
          onClick: onClose,
          role: 'button',
          'aria-label': 'close-promo-video',
        }}
      />
    </SheetWrapper>
  )
}
